import api from './api';

const formatValidationErrors = (detail) => {
  if (!Array.isArray(detail)) return null;

  const messages = detail.map((entry) => {
    const location = Array.isArray(entry?.loc) ? entry.loc.join('.') : null;
    const reason = entry?.msg || 'Invalid value';
    return location ? `${location}: ${reason}` : reason;
  });

  return messages.filter(Boolean).join(' | ') || null;
};

const extractErrorMessage = (payload, fallbackMessage) => {
  if (!payload) return fallbackMessage;

  if (typeof payload === 'string') {
    return payload;
  }

  // FastAPI often returns errors under `detail`.
  const detail = payload.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return formatValidationErrors(detail) || fallbackMessage;
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error || fallbackMessage;
  }

  if (typeof payload.message === 'string') {
    return payload.message;
  }

  return fallbackMessage;
};

// Global error handler for API responses
export const setupErrorHandling = (logoutCallback) => {
  // Response interceptor for handling errors
  const responseInterceptor = api.interceptors.response.use(
    (response) => response,
    (error) => {
      const { response } = error;
      
      if (response) {
        switch (response.status) {
          case 401:
            // Unauthorized - token expired or invalid
            console.warn('Authentication failed. Logging out...');
            logoutCallback();
            window.location.href = '/login';
            break;
            
          case 403:
            // Forbidden - user doesn't have permission
            console.warn('Access forbidden');
            break;
            
          case 404:
            // Not found
            console.warn('Resource not found');
            break;
            
          case 500:
            // Server error
            console.error('Server error occurred');
            break;
            
          default:
            console.error('API Error:', response.status, response.data);
        }
      } else {
        // Network error or other issues
        console.error('Network error:', error.message);
      }
      
      return Promise.reject(error);
    }
  );

  // Return cleanup function
  return () => {
    api.interceptors.response.eject(responseInterceptor);
  };
};

// Utility function to handle API errors in components
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  if (error.response) {
    // Server responded with error status
    return extractErrorMessage(error.response.data, defaultMessage);
  } else if (error.request) {
    // Request was made but no response received
    return 'Network error. Please check your connection.';
  } else {
    // Something else happened
    return extractErrorMessage(error, defaultMessage);
  }
};

// Utility function to check if error is authentication related
export const isAuthError = (error) => {
  return error.response?.status === 401 || error.response?.status === 403;
};
